/**
 * THE nutrient catalog: the user's "Master Sheet" (94 entries) in display order.
 *
 * One source of truth shared by:
 *   - the Worker (AI nutrient completion + validation),
 *   - the USDA importer (backend/food-import), which maps FoodData Central
 *     nutrient ids onto these keys,
 *   - the website (full nutrient panels), and
 *   - the Supabase seed for public.nutrient_defs (a test checks they match).
 *
 * All amounts are per the stated unit. Food rows store values PER 100 g of
 * the food as described (e.g. "raw" or "cooked, roasted").
 *
 * USDA ids: FoodData Central nutrient.id values, tried in order (first one a
 * food actually has wins). Some entries are derived instead (see `derive`).
 * Entries with no USDA id (allulose, BHB, phytate, ...) can only come from
 * labels, the user, or AI completion.
 */

export type NutrientGroup = 'general' | 'carbohydrates' | 'lipids' | 'protein' | 'vitamins' | 'minerals';
export type NutrientUnit = 'kcal' | 'g' | 'mg' | 'µg' | 'IU';

/** How a value was obtained. Stored per key in foods.nutrient_sources. */
export type NutrientSource = 'usda' | 'calc' | 'ai' | 'user' | 'label';

export interface NutrientDef {
  key: string;
  name: string;
  unit: NutrientUnit;
  group: NutrientGroup;
  /** Parent row for indentation and consistency checks. */
  parent: string | null;
  /**
   * true when this value is one of the parts that add up to (at most) the
   * parent: e.g. soluble + insoluble fiber <= fiber. false for rows that are
   * nested for display only (added sugars, carotenoids under vitamin A).
   */
  partOfParent: boolean;
  /** FoodData Central nutrient ids in priority order. */
  usda: readonly number[];
  /** Special rule the importer applies when no direct USDA id is present. */
  derive?: 'energy_atwater' | 'sum_children' | 'vit_d_from_ug' | 'sum_ids';
  /**
   * For derive = 'sum_ids': components added together. Each component is a
   * priority list of alternative ids for the same thing (first present wins),
   * so e.g. ALA measured two ways is never counted twice. Used only when at
   * least one component is present; absent components count as 0, so the
   * result is a lower bound and is marked 'calc'.
   */
  sumOf?: ReadonlyArray<readonly number[]>;
}

const d = (
  key: string, name: string, unit: NutrientUnit, group: NutrientGroup,
  parent: string | null, partOfParent: boolean, usda: readonly number[],
  extra: Partial<Pick<NutrientDef, 'derive' | 'sumOf'>> = {},
): NutrientDef => ({ key, name, unit, group, parent, partOfParent, usda, ...extra });

export const NUTRIENTS: readonly NutrientDef[] = [
  // ---- General --------------------------------------------------------------
  d('energy_kcal', 'Energy', 'kcal', 'general', null, false, [1008, 2048, 2047], { derive: 'energy_atwater' }),
  d('alcohol_g', 'Alcohol', 'g', 'general', null, false, [1018]),
  d('ash_g', 'Ash', 'g', 'general', null, false, [1007]),
  d('bhb_g', 'Beta-Hydroxybutyrate', 'g', 'general', null, false, []),
  d('caffeine_mg', 'Caffeine', 'mg', 'general', null, false, [1057]),
  d('oxalate_mg', 'Oxalate', 'mg', 'general', null, false, [1041]),
  d('phytate_mg', 'Phytate', 'mg', 'general', null, false, []),
  d('water_g', 'Water', 'g', 'general', null, false, [1051]),

  // ---- Carbohydrates --------------------------------------------------------
  d('carbs_g', 'Total Carbs', 'g', 'carbohydrates', null, false, [1005, 1050]),
  d('fiber_g', 'Fiber', 'g', 'carbohydrates', 'carbs_g', true, [1079, 2033]),
  d('fiber_insoluble_g', 'Insoluble Fiber', 'g', 'carbohydrates', 'fiber_g', true, [1084]),
  d('fiber_soluble_g', 'Soluble Fiber', 'g', 'carbohydrates', 'fiber_g', true, [1082]),
  d('starch_g', 'Starch', 'g', 'carbohydrates', 'carbs_g', true, [1009]),
  d('sugars_g', 'Sugars', 'g', 'carbohydrates', 'carbs_g', true, [2000, 1063], { derive: 'sum_children' }),
  d('allulose_g', 'Allulose', 'g', 'carbohydrates', 'sugars_g', false, []),
  d('fructose_g', 'Fructose', 'g', 'carbohydrates', 'sugars_g', true, [1012]),
  d('galactose_g', 'Galactose', 'g', 'carbohydrates', 'sugars_g', true, [1075]),
  d('glucose_g', 'Glucose', 'g', 'carbohydrates', 'sugars_g', true, [1011]),
  d('lactose_g', 'Lactose', 'g', 'carbohydrates', 'sugars_g', true, [1013]),
  d('maltose_g', 'Maltose', 'g', 'carbohydrates', 'sugars_g', true, [1014]),
  d('sucrose_g', 'Sucrose', 'g', 'carbohydrates', 'sugars_g', true, [1010]),
  d('added_sugars_g', 'Added Sugars', 'g', 'carbohydrates', 'sugars_g', false, [1235]),
  d('sugar_alcohols_g', 'Sugar Alcohols', 'g', 'carbohydrates', 'carbs_g', true, [1086]),

  // ---- Lipids ---------------------------------------------------------------
  d('fat_g', 'Total Fat', 'g', 'lipids', null, false, [1004, 1085]),
  d('mufa_g', 'Monounsaturated', 'g', 'lipids', 'fat_g', true, [1292]),
  d('pufa_g', 'Polyunsaturated', 'g', 'lipids', 'fat_g', true, [1293]),
  // Omega-3 = ALA + SDA (18:4) + EPA + DPA + DHA + 20:3 n-3, summed when any are measured.
  d('omega3_g', 'Omega-3', 'g', 'lipids', 'pufa_g', true, [], { derive: 'sum_ids', sumOf: [[1404, 1270], [1276], [1278], [1280], [1272], [1405]] }),
  d('ala_g', 'ALA', 'g', 'lipids', 'omega3_g', true, [1404, 1270]),
  d('dha_g', 'DHA', 'g', 'lipids', 'omega3_g', true, [1272]),
  d('epa_g', 'EPA', 'g', 'lipids', 'omega3_g', true, [1278]),
  // Omega-6 = LA + GLA + 20:2 n-6 + AA, summed when any are measured.
  d('omega6_g', 'Omega-6', 'g', 'lipids', 'pufa_g', true, [], { derive: 'sum_ids', sumOf: [[1316, 1269], [1321], [1313], [1271, 2022]] }),
  d('aa_g', 'AA (Arachidonic)', 'g', 'lipids', 'omega6_g', true, [1271, 2022]),
  d('la_g', 'LA (Linoleic)', 'g', 'lipids', 'omega6_g', true, [1316, 1269]),
  d('saturated_g', 'Saturated', 'g', 'lipids', 'fat_g', true, [1258]),
  d('trans_g', 'Trans-Fats', 'g', 'lipids', 'fat_g', true, [1257]),
  d('cholesterol_mg', 'Cholesterol', 'mg', 'lipids', null, false, [1253]),
  d('phytosterols_mg', 'Phytosterols', 'mg', 'lipids', null, false, [1283], { derive: 'sum_ids', sumOf: [[1285], [1286], [1287], [1288], [1289], [1294], [1296], [1298]] }),

  // ---- Protein & amino acids -----------------------------------------------
  d('protein_g', 'Total Protein', 'g', 'protein', null, false, [1003]),
  d('alanine_g', 'Alanine', 'g', 'protein', 'protein_g', true, [1222]),
  d('arginine_g', 'Arginine', 'g', 'protein', 'protein_g', true, [1220]),
  d('aspartic_acid_g', 'Aspartic acid', 'g', 'protein', 'protein_g', true, [1223]),
  d('cystine_g', 'Cystine', 'g', 'protein', 'protein_g', true, [1216, 1232]),
  d('glutamic_acid_g', 'Glutamic acid', 'g', 'protein', 'protein_g', true, [1224]),
  d('glycine_g', 'Glycine', 'g', 'protein', 'protein_g', true, [1225]),
  d('histidine_g', 'Histidine', 'g', 'protein', 'protein_g', true, [1221]),
  d('hydroxyproline_g', 'Hydroxyproline', 'g', 'protein', 'protein_g', true, [1228]),
  d('isoleucine_g', 'Isoleucine', 'g', 'protein', 'protein_g', true, [1212]),
  d('leucine_g', 'Leucine', 'g', 'protein', 'protein_g', true, [1213]),
  d('lysine_g', 'Lysine', 'g', 'protein', 'protein_g', true, [1214]),
  d('methionine_g', 'Methionine', 'g', 'protein', 'protein_g', true, [1215]),
  d('phenylalanine_g', 'Phenylalanine', 'g', 'protein', 'protein_g', true, [1217]),
  d('proline_g', 'Proline', 'g', 'protein', 'protein_g', true, [1226]),
  d('serine_g', 'Serine', 'g', 'protein', 'protein_g', true, [1227]),
  d('threonine_g', 'Threonine', 'g', 'protein', 'protein_g', true, [1211]),
  d('tryptophan_g', 'Tryptophan', 'g', 'protein', 'protein_g', true, [1210]),
  d('tyrosine_g', 'Tyrosine', 'g', 'protein', 'protein_g', true, [1218]),
  d('valine_g', 'Valine', 'g', 'protein', 'protein_g', true, [1219]),

  // ---- Vitamins -------------------------------------------------------------
  d('vit_b1_mg', 'B1 (Thiamine)', 'mg', 'vitamins', null, false, [1165]),
  d('vit_b2_mg', 'B2 (Riboflavin)', 'mg', 'vitamins', null, false, [1166]),
  d('vit_b3_mg', 'B3 (Niacin)', 'mg', 'vitamins', null, false, [1167]),
  d('vit_b5_mg', 'B5 (Pantothenic Acid)', 'mg', 'vitamins', null, false, [1170]),
  d('vit_b6_mg', 'B6 (Pyridoxine)', 'mg', 'vitamins', null, false, [1175]),
  d('vit_b12_ug', 'B12 (Cobalamin)', 'µg', 'vitamins', null, false, [1178]),
  d('biotin_ug', 'Biotin', 'µg', 'vitamins', null, false, [1176]),
  d('choline_mg', 'Choline', 'mg', 'vitamins', null, false, [1180]),
  // Dietary folate equivalents where USDA has them (the RDA unit); total folate otherwise.
  d('folate_ug', 'Folate', 'µg', 'vitamins', null, false, [1190, 1177]),
  d('vit_a_ug', 'Vitamin A (RAE)', 'µg', 'vitamins', null, false, [1106]),
  d('alpha_carotene_ug', 'Alpha-carotene', 'µg', 'vitamins', 'vit_a_ug', false, [1108]),
  d('beta_carotene_ug', 'Beta-carotene', 'µg', 'vitamins', 'vit_a_ug', false, [1107]),
  d('beta_cryptoxanthin_ug', 'Beta-cryptoxanthin', 'µg', 'vitamins', 'vit_a_ug', false, [1120]),
  d('lutein_zeaxanthin_ug', 'Lutein+Zeaxanthin', 'µg', 'vitamins', 'vit_a_ug', false, [1123], { derive: 'sum_ids', sumOf: [[1121], [1119]] }),
  d('lycopene_ug', 'Lycopene', 'µg', 'vitamins', 'vit_a_ug', false, [1122]),
  d('retinol_ug', 'Retinol', 'µg', 'vitamins', 'vit_a_ug', false, [1105]),
  d('vit_c_mg', 'Vitamin C', 'mg', 'vitamins', null, false, [1162]),
  d('vit_d_iu', 'Vitamin D', 'IU', 'vitamins', null, false, [1110], { derive: 'vit_d_from_ug' }),
  d('vit_e_mg', 'Vitamin E (alpha-tocopherol)', 'mg', 'vitamins', null, false, [1109]),
  d('beta_tocopherol_mg', 'Beta Tocopherol', 'mg', 'vitamins', 'vit_e_mg', false, [1125]),
  d('delta_tocopherol_mg', 'Delta Tocopherol', 'mg', 'vitamins', 'vit_e_mg', false, [1127]),
  d('gamma_tocopherol_mg', 'Gamma Tocopherol', 'mg', 'vitamins', 'vit_e_mg', false, [1126]),
  // K1 (phylloquinone) + K2 as menaquinone-4 when measured.
  d('vit_k_ug', 'Vitamin K', 'µg', 'vitamins', null, false, [], { derive: 'sum_ids', sumOf: [[1185], [1183]] }),

  // ---- Minerals -------------------------------------------------------------
  d('calcium_mg', 'Calcium', 'mg', 'minerals', null, false, [1087]),
  d('chromium_ug', 'Chromium', 'µg', 'minerals', null, false, [1096]),
  d('copper_mg', 'Copper', 'mg', 'minerals', null, false, [1098]),
  d('fluoride_ug', 'Fluoride', 'µg', 'minerals', null, false, [1099]),
  d('iodine_ug', 'Iodine', 'µg', 'minerals', null, false, [1100]),
  d('iron_mg', 'Iron', 'mg', 'minerals', null, false, [1089]),
  d('magnesium_mg', 'Magnesium', 'mg', 'minerals', null, false, [1090]),
  d('manganese_mg', 'Manganese', 'mg', 'minerals', null, false, [1101]),
  d('molybdenum_ug', 'Molybdenum', 'µg', 'minerals', null, false, [1102]),
  d('phosphorus_mg', 'Phosphorus', 'mg', 'minerals', null, false, [1091]),
  d('potassium_mg', 'Potassium', 'mg', 'minerals', null, false, [1092]),
  d('selenium_ug', 'Selenium', 'µg', 'minerals', null, false, [1103]),
  d('sodium_mg', 'Sodium', 'mg', 'minerals', null, false, [1093]),
  d('zinc_mg', 'Zinc', 'mg', 'minerals', null, false, [1095]),
];

export const GROUPS: ReadonlyArray<{ key: NutrientGroup; title: string }> = [
  { key: 'general', title: 'General' },
  { key: 'carbohydrates', title: 'Carbohydrates' },
  { key: 'lipids', title: 'Lipids' },
  { key: 'protein', title: 'Protein' },
  { key: 'vitamins', title: 'Vitamins' },
  { key: 'minerals', title: 'Minerals' },
];

export const NUTRIENT_BY_KEY: ReadonlyMap<string, NutrientDef> = new Map(NUTRIENTS.map((n) => [n.key, n]));
export const NUTRIENT_KEYS: readonly string[] = NUTRIENTS.map((n) => n.key);

/** Per-100 g ceiling for a value in this unit (physically impossible above it). */
export function maxPer100g(def: Pick<NutrientDef, 'unit' | 'key'>): number {
  switch (def.unit) {
    case 'kcal': return 902;          // pure fat ≈ 900 kcal/100 g
    case 'g': return 100;
    case 'mg': return 100_000;
    case 'µg': return 100_000_000;
    case 'IU': return 4_000_000;       // 100 µg vitamin D = 4,000 IU; allow concentrates
  }
}

/** Nutrient amounts keyed by catalog key. Missing key = unknown (NOT zero). */
export type NutrientMap = Record<string, number>;
export type NutrientSourceMap = Record<string, NutrientSource>;

/** Children whose values should add up to no more than the parent. */
export function partsOf(parentKey: string): NutrientDef[] {
  return NUTRIENTS.filter((n) => n.parent === parentKey && n.partOfParent);
}

/** How far the sum of parts may exceed the parent before we call it inconsistent. */
export function partsTolerance(parentKey: string): number {
  // Amino-acid totals can legitimately exceed "protein" because protein is
  // estimated from nitrogen × a conversion factor.
  if (parentKey === 'protein_g') return 1.3;
  // Omega-3/6 totals vs PUFA: CLA and isomer reporting cause small overlaps.
  if (parentKey === 'pufa_g') return 1.15;
  return 1.05;
}

/** Scale a per-100 g map to an amount in grams. */
export function scaleNutrients(per100g: Readonly<NutrientMap>, grams: number): NutrientMap {
  const f: number = grams / 100;
  const out: NutrientMap = {};
  for (const [k, v] of Object.entries(per100g)) out[k] = v * f;
  return out;
}

/** Add maps; a key present in any input is present in the output. */
export function addNutrients(...maps: ReadonlyArray<Readonly<NutrientMap>>): NutrientMap {
  const out: NutrientMap = {};
  for (const m of maps) for (const [k, v] of Object.entries(m)) if (Number.isFinite(v)) out[k] = (out[k] ?? 0) + v;
  return out;
}

/** Round for storage: enough precision for µg-level micronutrients. */
export function roundNutrient(v: number): number {
  if (v === 0) return 0;
  const abs: number = Math.abs(v);
  const digits: number = abs >= 100 ? 1 : abs >= 1 ? 2 : abs >= 0.01 ? 4 : 6;
  const f: number = 10 ** digits;
  return Math.round(v * f) / f;
}
